// components/HomeReviewCard.jsx
import { Box, Typography, Paper, Avatar, useMediaQuery, useTheme } from '@mui/material';
import { Rating } from '@mui/material';
import WomanIcon from '@mui/icons-material/Woman';
import ManIcon from '@mui/icons-material/Man';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';

function HomeReviewCard({ review }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!review) {
    return (
      <Paper sx={{ p: 2, height: '100%', minHeight: 380 }}>
        <Typography color="text.secondary">Отзыв временно недоступен</Typography>
      </Paper>
    );
  }

  const programTitle = review.programTitle || "Образовательная программа";
  const programType = review.programType || "Общее направление";
  const author = review.author || 'Аноним';
  const text = review.text || '';
  const rating = review.rating || 0;
  const gender = review.gender || 'other';
  const dateNewsFormat = review.dateNewsFormat || null;

  const getAvatarColor = () => {
    if (gender === 'female') return '#e91e63';
    if (gender === 'male') return '#2196f3';
    return '#9e9e9e';
  };

  const getAvatarIcon = () => {
    if (gender === 'female') return <WomanIcon />;
    if (gender === 'male') return <ManIcon />;
    return <PersonIcon />;
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('ru-RU', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch {
      return '';
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2.5,
        height: 450,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 20px rgba(0,0,0,0.12)'
        },
      }}
    >
      <Box 
        sx={{ 
          mb: 2,
          pb: 1,
          borderBottom: '2px solid',
          borderColor: 'primary.light',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <SchoolIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography 
            variant="subtitle2"
            color="primary.main" 
            fontWeight="bold"
            sx={{ 
              textTransform: 'uppercase', 
              letterSpacing: 0.5,
              fontSize: isMobile ? '0.75rem' : '0.85rem',
            }}
          >
            Программа
          </Typography>
        </Box>
        <Typography 
          variant="body2"
          fontWeight="bold" 
          sx={{ 
            lineHeight: 1.3,
            fontSize: isMobile ? '0.9rem' : '1rem',
            mt: 0.5
          }}
        >
          {programTitle}
        </Typography>
        {programType && programType !== "Общее направление" && (
          <Typography 
            variant="body2"
            color="text.secondary" 
            sx={{ 
              fontSize: isMobile ? '0.7rem' : '0.8rem',
              mt: 0.25
            }}
          >
            {programType}
          </Typography>
        )}
      </Box>

      <Box 
        sx={{ 
          flexGrow: 1,
          mb: 2,
          overflowY: 'auto',
          minHeight: 100,
          maxHeight: 180,
          pr: 0.5,
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '4px',
          },
        }}
      >
        <Typography 
          variant="body1"
          sx={{ 
            fontStyle: 'italic',
            lineHeight: 1.5,
            fontSize: isMobile ? '15px' : '16px',
            whiteSpace: 'pre-wrap',
          }}
        >
          {text ? `"${text}"` : 'Текст отзыва временно недоступен'}
        </Typography>
      </Box>

      {rating > 0 && (
        <Box sx={{ mb: 1.5, flexShrink: 0 }}>
          <Rating
            value={rating}
            readOnly
            precision={0.5}
            size="medium"
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          pt: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Avatar 
          sx={{ 
            width: 40,
            height: 40,
            backgroundColor: getAvatarColor(),
            mr: 1.5
          }}
        >
          {getAvatarIcon()}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" fontSize={isMobile ? '0.9rem' : '1rem'}>
            {author}
          </Typography>
          {dateNewsFormat && (
            <Typography variant="body2" color="text.secondary" fontSize={isMobile ? '0.7rem' : '0.8rem'}>
              {formatDate(dateNewsFormat)}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default HomeReviewCard;